import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';
import { faqs, relatedGuides } from './data';

export default function PacayaVolcano() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': 'https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua#article',
        headline: 'Pacaya Volcano Day Trip from Antigua: Complete Guide',
        description: 'Complete guide to hiking Pacaya volcano from Antigua Guatemala. Difficulty, cost, what to expect, safety tips, and how to book the best tour.',
        image: 'https://public.readdy.ai/ai/img_res/a21b68b7e689d59b802a9bccd0cb22c1.jpg',
        datePublished: '2026-02-20',
        dateModified: '2026-02-20',
        author: {
          '@type': 'Organization',
          name: 'La Antigua Guatemala Travel Guide'
        },
        publisher: {
          '@type': 'Organization',
          name: 'La Antigua Guatemala Travel Guide',
          logo: {
            '@type': 'ImageObject',
            url: 'https://la-antigua-guatemala.com/logo.png'
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': 'https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua'
        },
        inLanguage: 'en-US'
      },
      {
        '@type': 'BreadcrumbList',
        '@id': 'https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua#breadcrumb',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://la-antigua-guatemala.com'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Day Trips',
            item: 'https://la-antigua-guatemala.com/day-trips-from-antigua-guatemala'
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Pacaya Volcano',
            item: 'https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua'
          }
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': 'https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua#faq',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Pacaya Volcano Day Trip from Antigua (Complete Guide)"
        description="Complete guide to hiking Pacaya volcano from Antigua Guatemala. Difficulty, cost, what to expect, safety tips, and how to book the best tour."
        canonical="https://la-antigua-guatemala.com/pacaya-volcano-day-trip-from-antigua"
        keywords="pacaya volcano day trip from antigua, pacaya volcano hike from antigua, pacaya tour antigua, how hard is pacaya volcano hike, pacaya volcano difficulty, pacaya vs acatenango, best volcano hike antigua, pacaya volcano tour cost, what to wear pacaya volcano, is pacaya volcano safe, antigua guatemala travel guide"
        type="article"
        schema={schema}
        lastModified="2026-02-20"
        ogImage={{
          url: 'https://readdy.ai/api/search-image?query=Pacaya%20volcano%20Guatemala%20dramatic%20landscape%20with%20hikers%20approaching%20active%20lava%20flows%2C%20glowing%20red%20molten%20rock%20visible%20through%20dark%20volcanic%20surface%2C%20steam%20rising%20from%20vents%2C%20accessible%20day%20hike%20adventure%2C%20tourists%20near%20lava%20field%2C%20stunning%20volcanic%20scenery%20with%20clear%20blue%20sky&width=1200&height=630&seq=pacaya-hero-og&orientation=landscape',
          width: 1200,
          height: 630
        }}
      />
      
      <Navbar darkText />
      
      <div className="pt-20">
        {/* Hero */}
        <section className="relative h-[60vh] flex items-center justify-center mt-20">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Pacaya%20volcano%20Guatemala%20dramatic%20landscape%20with%20hikers%20approaching%20active%20lava%20flows%2C%20glowing%20red%20molten%20rock%20visible%20through%20dark%20volcanic%20surface%2C%20steam%20rising%20from%20vents%2C%20accessible%20day%20hike%20adventure%2C%20tourists%20near%20lava%20field%2C%20stunning%20volcanic%20scenery%20with%20clear%20blue%20sky&width=1920&height=800&seq=pacaya-hero&orientation=landscape"
              alt="Pacaya Volcano Day Trip from Antigua Guatemala - hikers near active lava flows"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Pacaya Volcano Day Trip from Antigua: Complete Guide</h1>
            <p className="text-xl md:text-2xl">Hike to active lava flows on Guatemala's most accessible volcano</p>
          </div>
        </section>

        {/* Breadcrumbs */}
        <Breadcrumbs 
          items={[
            { label: 'Day Trips', path: '/day-trips-from-antigua-guatemala' },
            { label: 'Pacaya Volcano', path: '/pacaya-volcano-day-trip-from-antigua' }
          ]}
        />

        {/* Quick Take */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-sm border-l-4 border-teal-500 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Take</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Duration</p>
                  <p className="text-gray-900 font-medium">5-6 hours total (3-4 hours hiking)</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Difficulty</p>
                  <p className="text-gray-900 font-medium">Easy to Moderate</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Elevation Gain</p>
                  <p className="text-gray-900 font-medium">~1,200 feet (365 meters)</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Cost</p>
                  <p className="text-gray-900 font-medium">$50-60 USD (tour from Antigua)</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Best For</p>
                  <p className="text-gray-900 font-medium">First-time volcano hikers, families, short trips</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-teal-600 mb-1">Skip If</p>
                  <p className="text-gray-900 font-medium">You can't walk uphill for 90 minutes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Overview */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Is Pacaya Volcano Worth It?</h2>
            
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>Yes, absolutely.</strong> The Pacaya volcano day trip from Antigua is one of the best-value activities in Guatemala. For $50-60 and just half a day, you get to hike an active volcano, see glowing lava flows up close, roast marshmallows over volcanic heat, and experience one of nature's most powerful forces. It's accessible enough for most fitness levels but still feels like a genuine adventure.
            </p>

            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Unlike many volcano hikes that just offer views from a distance, Pacaya lets you get remarkably close to active lava. You'll see red-hot molten rock glowing through cracks in the cooled surface, feel the intense heat radiating from lava vents, and watch steam rise from the volcanic landscape. It's one of the most accessible active lava experiences anywhere in the world.
            </p>

            <p className="text-xl text-gray-700 leading-relaxed">
              The hike itself is challenging enough to feel rewarding but not so difficult that it excludes casual hikers. Most people in reasonable shape can complete it, including families with older kids. If you're planning your trip logistics, check our <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:text-teal-600 font-medium">airport transfer guide</Link> to get from Guatemala City to Antigua smoothly before your volcano adventure.
            </p>
          </div>
        </section>

        {/* What Makes Pacaya Unique */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">What Makes Pacaya Volcano Unique</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-fire-line text-3xl text-orange-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Active Lava Flows</h3>
                <p className="text-gray-700">Get within feet of glowing red lava. You'll see molten rock through surface cracks and feel the intense heat—an experience few volcanoes offer safely.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-walk-line text-3xl text-teal-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Accessible Difficulty</h3>
                <p className="text-gray-700">Unlike extreme volcano hikes, Pacaya is doable for most fitness levels. No technical climbing, no overnight camping, no extreme altitude.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-time-line text-3xl text-amber-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Half-Day Adventure</h3>
                <p className="text-gray-700">Perfect for short trips. The entire tour takes just 5-6 hours, leaving you the rest of the day to explore Antigua or relax.</p>
              </div>
            </div>

            <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
              <div className="flex items-start">
                <i className="ri-lightbulb-line text-2xl text-amber-600 mt-1 mr-3"></i>
                <div>
                  <p className="font-bold text-gray-900 mb-2">Pro Tip</p>
                  <p className="text-gray-700">Pacaya is constantly active, meaning you're virtually guaranteed to see lava. Some volcanoes have dormant periods—Pacaya doesn't. This reliability makes it perfect for travelers on tight schedules who want to ensure they get the full volcano experience.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Hard Is the Hike - Adding full difficulty section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">How Hard Is the Pacaya Volcano Hike?</h2>
            
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              <strong>Pacaya is rated easy to moderate.</strong> The hike takes 1.5-2 hours uphill with an elevation gain of about 1,200 feet (365 meters). Most of the trail is gradual and manageable, but the final 20-30 minutes get steeper with loose volcanic rock underfoot. If you can walk uphill steadily for 90 minutes with occasional breaks, you can do Pacaya.
            </p>

            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Difficulty Breakdown</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Physical Fitness Required</span>
                    <span className="text-teal-600 font-medium">Moderate</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-teal-500 h-3 rounded-full" style={{width: '60%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">You need to be comfortable walking uphill for 90 minutes. No extreme fitness required.</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Technical Difficulty</span>
                    <span className="text-teal-600 font-medium">Low</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-teal-500 h-3 rounded-full" style={{width: '25%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">No climbing or scrambling. Just walking on established trails with some loose rock.</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Altitude Challenge</span>
                    <span className="text-teal-600 font-medium">Low</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-teal-500 h-3 rounded-full" style={{width: '30%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Summit is 8,373 feet. Most people don't experience altitude sickness at this elevation.</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Overall Challenge</span>
                    <span className="text-teal-600 font-medium">Easy-Moderate</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-teal-500 h-3 rounded-full" style={{width: '45%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Suitable for most travelers in reasonable health. Families with kids 10+ often complete it successfully.</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-teal-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">You Can Probably Do Pacaya If:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You can walk uphill for 90 minutes with breaks</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You're comfortable on uneven terrain</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You've done any hiking before (even easy trails)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You're in generally good health</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You're okay with some physical challenge</span>
                  </li>
                </ul>
              </div>

              <div className="bg-rose-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Skip Pacaya If:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <i className="ri-close-line text-rose-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You have serious mobility issues</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-rose-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You can't walk uphill for more than 15 minutes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-rose-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You have severe respiratory or heart conditions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-rose-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You're pregnant (volcanic gases and terrain)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-rose-500 mt-1 mr-2"></i>
                    <span className="text-gray-700">You want a completely flat, easy walk</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
              <div className="flex items-start">
                <i className="ri-lightbulb-line text-2xl text-amber-600 mt-1 mr-3"></i>
                <div>
                  <p className="font-bold text-gray-900 mb-2">Pro Tip</p>
                  <p className="text-gray-700">If you're worried about the difficulty, you can rent a horse for $15-25 to ride most of the way up. The horse takes you to about 80% of the summit, then you walk the final section (which is too steep for horses). This is a great option for older travelers, those with knee issues, or families with younger kids.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Plan Your Antigua Trip?</h2>
            <p className="text-xl text-white/90 mb-8">Get personalized recommendations for volcano hikes, day trips, and the perfect Antigua itinerary.</p>
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
              Plan My Trip
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-10">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-gray-100 transition"
                  >
                    <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-teal-500 flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <RelatedGuides guides={relatedGuides} />

        {/* Final CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Plan Your Antigua Adventure?</h2>
            <p className="text-xl text-white/90 mb-8">Get personalized recommendations for volcano hikes, day trips, and the perfect itinerary for your Guatemala trip.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
                Plan My Trip
              </Link>
              <Link to="/acatenango-hike-from-antigua" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
                Acatenango Hike Guide
              </Link>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">
              Last updated: February 2026 | Written by the La Antigua Guatemala travel team | 
              <Link to="/editorial-policy" className="text-teal-500 hover:text-teal-600 ml-2">Editorial Policy</Link>
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}